import { useMediaQuery, useTheme } from "@mui/material";
import { getDocumentStatus } from "@repo/types";
import {
  ACCEPTED_IMAGE_TYPES,
  ACCEPTED_PDF_TYPES,
  DEFAULT_S3_REGION,
  formatDate,
  isImageFile,
  isPdfFile,
  resolveStoredMediaFileUrl,
} from "@repo/utils";
import { useMemo, useState } from "react";
import type { Accept } from "react-dropzone";
import { useDropzone } from "react-dropzone";
import useUploadFile from "@/hooks/use-upload-file";
import { downloadFromUrl } from "@/utils/download";
import { getDocumentUrl } from "@/utils/getData";
import type { Document, UploadMetadata } from "./types";
import { getDocumentActionStatus } from "./use-document-actions";

type DocumentRowMode = "upload" | "review";

type UseDocumentRowOptions = {
  value?: Document | null;
  meta: UploadMetadata;
  mode?: DocumentRowMode;
  canEdit?: boolean;
  onFileUploaded?: (file: Document) => void;
};

export function useDocumentRow({
  value,
  meta,
  mode = "upload",
  canEdit = true,
  onFileUploaded,
}: UseDocumentRowOptions) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [openPreview, setOpenPreview] = useState(false);

  const statusResult = useMemo(() => getDocumentStatus(value), [value]);

  const resolvedUrl = useMemo(() => {
    if (!value) return "";
    if (mode === "review") {
      const mainUrl = (value?.url ?? "").trim();
      if (mainUrl && /^https?:\/\//i.test(mainUrl)) return mainUrl;
      // Server-managed documents require the exact URL supplied by the API.
      if (value.version_status) return "";
      const bucket = import.meta.env.VITE_S3_BUCKET as string | undefined;
      if (!bucket) return "";
      return (
        resolveStoredMediaFileUrl(value, {
          bucket,
          region: (import.meta.env.VITE_AWS_REGION as string | undefined) || DEFAULT_S3_REGION,
        }) ?? ""
      );
    }

    const docUrl = getDocumentUrl(value).trim();
    if (docUrl && /^https?:\/\//i.test(docUrl)) return docUrl;
    if (value.version_status || statusResult.status !== "APPROVED") return "";
    const bucket = import.meta.env.VITE_S3_BUCKET as string | undefined;
    if (!bucket) return docUrl;
    return (
      resolveStoredMediaFileUrl(value, {
        bucket,
        region: (import.meta.env.VITE_AWS_REGION as string | undefined) || DEFAULT_S3_REGION,
      }) ?? docUrl
    );
  }, [value, statusResult.status, mode]);

  const documentUrl = resolvedUrl;

  const actions = getDocumentActionStatus(value ?? undefined, documentUrl, canEdit);

  const isImage = isImageFile({
    type: value?.type,
    tempObjectUrl: actions.documentUrl || documentUrl,
  });
  const isPDF = isPdfFile({
    type: value?.type,
    tempObjectUrl: actions.documentUrl || documentUrl,
  });

  const { loading, uploadProgress, onDrop } = useUploadFile({
    maxFileSize: 10,
    maxFiles: 1,
    uploadedFiles: [],
    metadata: { ...meta },
    errorMessages: {
      maxSize: "Le fichier est trop volumineux (max 10MB).",
      fileType: "Type de fichier non supporté.",
      maxFiles: "Un seul fichier est autorisé.",
    },
    fileName: value?.label ?? undefined,
    onFilesUploaded: (newFiles) => {
      if (newFiles.length > 0) {
        const uploadedFile = newFiles[0];
        const mergedMetadata = {
          ...(value?.metadata ?? {}),
          ...(uploadedFile.metadata ?? {}),
        };
        const newDocument: Document = {
          label: value?.label ?? uploadedFile.name,
          name: uploadedFile.name,
          key: uploadedFile.key,
          url: uploadedFile.url,
          type: uploadedFile.originalFile.type,
          updatedAt: new Date().toISOString(),
          metadata: Object.keys(mergedMetadata).length > 0 ? mergedMetadata : undefined,
          // Admin uploads should immediately show as APPROVED
          version_status: "APPROVED",
          document_state: "VALID",
        };
        void Promise.resolve().then(() => onFileUploaded?.(newDocument));
      }
    },
  });

  const documentTitle = value?.label ?? value?.name ?? "Untitled Document";
  const hasDocument = Boolean(actions.documentUrl || documentUrl);
  const updatedAt = value?.updatedAt ? formatDate(value.updatedAt) : "—";
  const isDragDropEnabled = actions.canChange && !loading;
  const acceptedFileTypes: Accept = isPDF ? ACCEPTED_PDF_TYPES : ACCEPTED_IMAGE_TYPES;

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop: (...args) => void onDrop(...args),
    noClick: true,
    noKeyboard: true,
    multiple: false,
    disabled: !isDragDropEnabled,
    accept: acceptedFileTypes,
  });

  const handleDownload = () => downloadFromUrl(documentUrl || actions.documentUrl, documentTitle);

  return {
    isMobile,
    openPreview,
    setOpenPreview,
    statusResult,
    documentUrl,
    actions,
    isImage,
    isPDF,
    loading,
    uploadProgress,
    documentTitle,
    hasDocument,
    updatedAt,
    isDragDropEnabled,
    getRootProps,
    getInputProps,
    isDragActive,
    open,
    handleDownload,
  };
}
